// Company standards (fleet-3): the adoption grid, vessel x standard -> Not sent / Draft / Active / Changed locally / Review overdue.
// Plain JS, shared by the Fleet > Standards tab, the AI's fleet_standards tool (api/_agent.js) and tests/fleet-3.test.mjs.
// Input: the company's standards (company_standards rows), its vessels, and the copies' state on board (hc_standard_copies_state(),
// sql/fleet-3.sql: one row per copy with state draft / active / archived / missing, changed, review_due).

export const KIND_LABEL = { ra: 'Risk assessment', sop: 'SOP', checklist: 'Checklist', manual: 'Manual' }
export const KIND_SHORT = { ra: 'RA', sop: 'SOP', checklist: 'Checklist', manual: 'Manual' }
export const KINDS = Object.keys(KIND_LABEL)

// the five statuses, in the order the grid counts them; tone -> the page's colour
export const STATUS = {
  not_sent: { label: 'Not sent', short: 'Not sent', tone: 'muted' },
  draft: { label: 'Draft', short: 'Draft', tone: 'amber' },
  active: { label: 'Active', short: 'Active', tone: 'green' },
  changed: { label: 'Changed locally', short: 'Changed', tone: 'purple' },
  review: { label: 'Review overdue', short: 'Review', tone: 'red' },
}
export const STATUSES = Object.keys(STATUS)
// "adopted" = set Active on board (also when changed there, or when its review is overdue)
export const ADOPTED = new Set(['active', 'changed', 'review'])

// where a copy opens inside the vessel: the page and the key that page reads once (src/pages/RiskAssessments.jsx hc_open_ra, Sops.jsx
// hc_open_sop, Checklists.jsx hc_open_checklist, Manuals.jsx hc_open_manual)
export const OPEN = {
  ra: { page: 'risk_assessments', key: 'hc_open_ra' },
  sop: { page: 'sops', key: 'hc_open_sop', extra: { hc_open_sop_mode: 'read' } },
  checklist: { page: 'checklists', key: 'hc_open_checklist' },
  manual: { page: 'manuals', key: 'hc_open_manual' },
}

const iso = v => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v == null ? '' : v instanceof Date ? v.toISOString() : v)); return m ? m[0] : null }
const live = c => c.state === 'draft' || c.state === 'active'
const byVersion = (a, b) => b.version - a.version

// one cell. copies: this standard's copies on this vessel (any version). std: the standard (current_version). today: 'YYYY-MM-DD'
// -> { status, label, detail, version, copy (the one a click opens), outdated (a newer version exists that this vessel has not got) }
export function cellOf(copies, std, today) {
  const mine = [...(copies || [])].sort(byVersion)
  const cur = (std && std.current_version) || 0
  const on = mine.filter(live)
  if (!on.length) {
    const last = mine[0]
    const why = !last ? '' : last.state === 'missing' ? 'v' + last.version + ' deleted on board' : 'v' + last.version + ' archived on board'
    return { status: 'not_sent', label: STATUS.not_sent.label, detail: why, version: null, copy: null, outdated: false }
  }
  const top = on[0], act = on.find(c => c.state === 'active')
  if (top.state === 'draft') {
    return { status: 'draft', label: STATUS.draft.label, detail: 'v' + top.version + ' waiting for the Master' + (act ? ' · v' + act.version + ' active' : ''), version: top.version, copy: top, outdated: top.version < cur }
  }
  const due = iso(top.review_due)
  const outdated = top.version < cur
  const tail = outdated ? ' · v' + cur + ' not sent' : ''
  if (due && today && due < today) return { status: 'review', label: STATUS.review.label, detail: 'v' + top.version + ' · review was due ' + due + tail, version: top.version, copy: top, outdated }
  if (top.changed) return { status: 'changed', label: STATUS.changed.label, detail: 'v' + top.version + ' · edited on board since it was sent' + tail, version: top.version, copy: top, outdated }
  return { status: 'active', label: STATUS.active.label, detail: 'v' + top.version + (top.accepted_by ? ' · accepted by ' + top.accepted_by : '') + tail, version: top.version, copy: top, outdated }
}

// the grid: one row per standard (archived ones left out), one cell per vessel; counts per status; adopted = Active on board
export function buildGrid(standards, fleet, copies, today) {
  const by = new Map()
  for (const c of copies || []) { const k = c.standard_id + '|' + c.client_id; if (!by.has(k)) by.set(k, []); by.get(k).push(c) }
  return (standards || []).filter(s => !s.archived_at).map(s => {
    const cells = {}
    for (const v of fleet || []) cells[v.id] = cellOf(by.get(s.id + '|' + v.id), s, today)
    const counts = Object.fromEntries(STATUSES.map(k => [k, 0]))
    Object.values(cells).forEach(c => { counts[c.status]++ })
    const adopted = Object.values(cells).filter(c => ADOPTED.has(c.status)).length
    return { standard: s, cells, counts, adopted, of: (fleet || []).length }
  }).sort((a, b) => KINDS.indexOf(a.standard.kind) - KINDS.indexOf(b.standard.kind) || String(a.standard.code || a.standard.title).localeCompare(String(b.standard.code || b.standard.title), 'en', { numeric: true }))
}

export const filterGrid = (rows, kind = 'all', status = 'all') => (rows || []).filter(r => (kind === 'all' || r.standard.kind === kind) && (status === 'all' || Object.values(r.cells).some(c => c.status === status)))

export function gridTotals(rows) {
  const t = Object.fromEntries(STATUSES.map(k => [k, 0]))
  for (const r of rows || []) for (const k of STATUSES) t[k] += r.counts[k]
  return t
}

// the Excel sheet: [[header...], [values...]]: one line per standard, a column per vessel ("Draft (v2 waiting for the Master)")
export function gridTable(rows, fleet) {
  const head = ['Code', 'Standard', 'Type', 'Version', 'Adopted', ...(fleet || []).map(v => v.vessel_name)]
  return [head, ...(rows || []).map(r => [r.standard.code || '', r.standard.title, KIND_LABEL[r.standard.kind] || r.standard.kind, 'v' + r.standard.current_version, r.adopted + ' of ' + r.of,
    ...(fleet || []).map(v => { const c = r.cells[v.id]; return c ? c.label + (c.detail ? ' (' + c.detail + ')' : '') : '' })])]
}

// ---- the AI: "which vessels haven't adopted the tender launch RA?" ----
const norm = s => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
const KIND_WORDS = { ra: /\b(ra|ras|risk|assessments?)\b/, sop: /\b(sops?|procedures?)\b/, checklist: /\bchecklists?\b/, manual: /\bmanuals?\b/ }
const STOP = new Set(['the', 'a', 'an', 'our', 'for', 'of', 'to', 'and', 'on', 'which', 'what', 'vessels', 'vessel', 'yachts', 'yacht', 'adopted', 'adopt', 'not', 'hasnt', 'havent', 'standard', 'company',
  'ra', 'ras', 'risk', 'assessment', 'assessments', 'sop', 'sops', 'procedure', 'procedures', 'checklist', 'checklists', 'manual', 'manuals'])
// the standards a question names: by code (BM-RA-01) or by title words; the kind word (RA, SOP...) narrows it. [] = none named.
export function matchStandards(standards, question) {
  const q = norm(question), list = (standards || []).filter(s => !s.archived_at)
  const code = list.filter(s => s.code && q.includes(norm(s.code)))
  if (code.length) return code
  const kinds = KINDS.filter(k => KIND_WORDS[k].test(q))
  const words = q.split(' ').filter(w => w.length > 1 && !STOP.has(w))
  const pool = kinds.length ? list.filter(s => kinds.includes(s.kind)) : list
  if (!words.length) return kinds.length ? pool : []
  const stem = w => w.replace(/(ing|es|s)$/, '')
  const scored = pool.map(s => { const t = norm(s.title + ' ' + (s.code || '')).split(' ').map(stem); return { s, n: words.filter(w => t.includes(stem(w))).length } }).filter(x => x.n > 0)
  const best = Math.max(0, ...scored.map(x => x.n))
  return scored.filter(x => x.n === best).map(x => x.s)
}

// per standard: which vessels have it Active on board, and which not (with the status and why) - the fleet_standards tool's answer
export function adoptionAnswer(rows, fleet) {
  return (rows || []).map(r => ({
    standard: (r.standard.code ? r.standard.code + ' ' : '') + r.standard.title, type: KIND_LABEL[r.standard.kind], version: r.standard.current_version,
    adopted: (fleet || []).filter(v => ADOPTED.has(r.cells[v.id].status)).map(v => v.vessel_name + (r.cells[v.id].status === 'active' ? '' : ' (' + r.cells[v.id].label.toLowerCase() + ')')),
    not_adopted: (fleet || []).filter(v => !ADOPTED.has(r.cells[v.id].status)).map(v => ({ vessel: v.vessel_name, status: r.cells[v.id].label, detail: r.cells[v.id].detail || undefined })),
    summary: r.adopted + ' of ' + r.of + ' vessels have it Active on board',
  }))
}
